import { useCallback, useMemo, useRef, useState } from 'react';
import { isEqual, kebabCase } from 'lodash';
import { useQuery } from '@tanstack/react-query';
import { Box, Typography, Tooltip, Zoom, Popper, PopperProps, Stack } from '@mui/material';
import {
  configureQuery,
  ILayoutComponent,
  createLayoutComponentObject,
  LayoutComponentNames,
  ILayoutComponentSettings,
  getResponseErrorMessage,
  useAssistantLoadingStore,
} from '@diana/core';
import {
  ContainerTypeComponent,
  FieldTypeComponent,
  useDragLayoutComponent,
} from '../../../../../providers/DndLayoutComponents2';
import { useDndComponentDragEndEvent } from '../../../Views/BuilderView/ComponentBuilder/useDndComponentDragEndEvent';
import { useFlowBuilder2PageStore } from '../../../Store/useFlowBuilder2PageStore';
import { FlowBuilderModalContentTypeEnum } from '../../../Store/FlowBuilder2PageStore.types';
import { StyledCard } from './ComponentsList.styles';
import { useGetComponentDefaultPropertiesBySchema } from '../../../Support/useGetComponentDefaultPropertiesBySchema';
import { isSectionComponent } from '../../../Support';

export interface IComponentsListDrawerItemProps {
  name: LayoutComponentNames;
  settings: ILayoutComponentSettings;
}

export const ComponentsListItem = (props: IComponentsListDrawerItemProps) => {
  const { name, settings } = props;
  const { displayName, icon: ComponentIcon, matchProperties, renderTooltip } = settings;

  const schemaQuery = configureQuery.getComponentSchema({ schemaName: name });
  const {
    data: schema,
    isLoading: isLoadingSchema,
    error: schemaError,
  } = useQuery({
    queryKey: schemaQuery.key,
    queryFn: schemaQuery.fn,
    gcTime: Infinity,
    staleTime: Infinity,
  });

  const [suppressTooltip, setSuppressTooltip] = useState<boolean>(false);
  const bodyRef = useRef<HTMLDivElement>(null);

  const setActiveModal = useFlowBuilder2PageStore((s) => s.setActiveModal);
  const hasAnyPendingStatus = useFlowBuilder2PageStore((s) => s.hasAnyPendingStatus(), isEqual);
  const isChatLoading = useAssistantLoadingStore((state) => state.isLoading);

  const getComponentDefaultPropertiesBySchema = useGetComponentDefaultPropertiesBySchema();

  const defaultProperties = useMemo(
    () => getComponentDefaultPropertiesBySchema(schema),
    [getComponentDefaultPropertiesBySchema, schema],
  );

  const type = useMemo(
    () => (isSectionComponent({ component: name }) ? ContainerTypeComponent : FieldTypeComponent),
    [name],
  );

  const TooltipPopper = useMemo(() => {
    return (props: PopperProps) => {
      const { children } = props;
      return (
        <Popper
          {...props}
          data-testid={kebabCase(`${displayName} Component Tooltip`)}
          sx={({ zIndex }) => ({
            zIndex: zIndex.tooltip,
            '.MuiTooltip-tooltip': { border: 'none', backgroundColor: 'none', p: 0, mx: 1 },
          })}
        >
          {children}
        </Popper>
      );
    };
  }, [displayName]);

  const buildLayoutComponent = useCallback((): ILayoutComponent => {
    const layoutComponent = createLayoutComponentObject({
      component: name,
      properties: { ...defaultProperties, ...matchProperties },
    });
    layoutComponent.name = '';
    layoutComponent.dataPath = '';
    layoutComponent.dataPathUpdate = 'Auto';
    return layoutComponent;
  }, [name, defaultProperties, matchProperties]);

  const renderToolTipContent = () => {
    if (suppressTooltip) {
      return null;
    }

    if (schemaError) {
      return (
        <Box px={2} py={1}>
          <Typography color="error">{getResponseErrorMessage(schemaError)}</Typography>
        </Box>
      );
    }

    return renderTooltip?.() ?? null;
  };

  const { onDndComponentDragEnd } = useDndComponentDragEndEvent();
  const [{ isDragging }, drag, preview] = useDragLayoutComponent<{ isDragging: boolean }>(
    {
      type,
      canDrag: () => !schemaError && !isLoadingSchema,
      item: () => {
        const layoutComponent = buildLayoutComponent();
        setSuppressTooltip(true);
        return {
          origin: null,
          location: null,
          component: layoutComponent,
          descendants: [],
          originalComponent: layoutComponent,
        };
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: (dragObject, monitor) => {
        setSuppressTooltip(false);
        onDndComponentDragEnd({ dragObject, monitor });
      },
    },
    [type, schemaError, isLoadingSchema, buildLayoutComponent, onDndComponentDragEnd],
  );

  drag(bodyRef);
  preview(bodyRef);

  return (
    <Tooltip
      TransitionProps={{ timeout: 200 }}
      TransitionComponent={Zoom}
      disableInteractive
      enterDelay={500}
      enterNextDelay={500}
      title={renderToolTipContent()}
      slots={{ popper: TooltipPopper }}
    >
      <Box
        sx={({ palette, spacing }) => ({
          flex: 1,
          borderRadius: spacing(0.5),
          backgroundColor: isDragging ? palette.action.hover : undefined,
        })}
      >
        <StyledCard
          ref={bodyRef}
          error={!!schemaError}
          loading={isLoadingSchema}
          disabled={isChatLoading}
          isDragging={isDragging}
          onMouseDown={() => {
            if (hasAnyPendingStatus) {
              return setActiveModal({
                name: FlowBuilderModalContentTypeEnum.AiAcceptReject,
                context: {},
              });
            }
          }}
          className="contained"
          data-testid={kebabCase(`${displayName} Component`)}
        >
          <Stack
            alignItems="center"
            p={1.5}
            gap={0.75}
            sx={{
              cursor: isLoadingSchema
                ? 'wait'
                : schemaError || isChatLoading
                ? 'not-allowed'
                : 'grab',
              textAlign: 'center',
            }}
          >
            <ComponentIcon sx={{ width: 20, height: 20 }} />
            <Typography whiteSpace="nowrap" width="100%" overflow="hidden" textOverflow="ellipsis">
              {displayName}
            </Typography>
          </Stack>
        </StyledCard>
      </Box>
    </Tooltip>
  );
};
